import type { Entorno } from './detectarPlataforma';

/**
 * Regla de la franja de invitación a instalar.
 *
 * La marca lleva el id del usuario adentro, igual que el tour de bienvenida
 * (`useWelcomeTour.ts`) y que el lumisial persistido: instalar es una acción por
 * dispositivo, pero en un equipo compartido cada persona merece su propia invitación.
 */

const PREFIJO = 'invitacionInstalarApp:v1';
const claveDe = (idUsuario: string) => `${PREFIJO}_${idUsuario}`;

/**
 * "Atendida" = el usuario hizo algo con la franja: la cerró, usó su botón, o instaló.
 * Mostrarla no la gasta — si la ignora, vuelve a aparecer en la sesión siguiente.
 */
export function yaSeAtendioLaInvitacion(idUsuario: string): boolean {
  try {
    return localStorage.getItem(claveDe(idUsuario)) === '1';
  } catch {
    // Sin almacenamiento no hay forma de recordar que ya la atendió; se prefiere no
    // invitar antes que invitar en cada carga para siempre.
    return true;
  }
}

export function marcarInvitacionAtendida(idUsuario: string): void {
  try {
    localStorage.setItem(claveDe(idUsuario), '1');
  } catch {
    /* almacenamiento no disponible: degradación aceptable */
  }
}

export interface CondicionesInvitacion {
  /** `usuario.id`; null mientras la sesión aún no cargó. */
  readonly idUsuario: string | null;
  /** Consta que la app ya está instalada en este equipo. */
  readonly yaInstalada: boolean;
  /** El tour de bienvenida está cubriendo la pantalla en este momento. */
  readonly tourActivo: boolean;
}

export function correspondeInvitar(condiciones: CondicionesInvitacion): boolean {
  const { idUsuario, yaInstalada, tourActivo } = condiciones;
  if (!idUsuario) return false;
  if (yaInstalada) return false;
  if (tourActivo) return false;
  return !yaSeAtendioLaInvitacion(idUsuario);
}

export interface TextoInvitacion {
  readonly mensaje: string;
  readonly rotuloBoton: string;
}

/**
 * En Firefox de escritorio no existe instalación de apps web, así que ni el mensaje
 * ni el botón pueden usar la palabra "instalar": lo que se consigue ahí es un acceso
 * directo en el escritorio.
 */
export function textoDeInvitacion(entorno: Entorno): TextoInvitacion {
  if (entorno.plataforma === 'firefox-escritorio') {
    return {
      mensaje: 'Deja Gnosis App a un doble clic desde tu escritorio.',
      rotuloBoton: 'Ver cómo',
    };
  }
  if (entorno.equipo === 'telefono') {
    return {
      mensaje: 'Instala Gnosis App en tu teléfono y ábrela desde tu pantalla de inicio.',
      rotuloBoton: 'Instalar',
    };
  }
  if (entorno.equipo === 'tablet') {
    return {
      mensaje: 'Instala Gnosis App en tu tablet y ábrela desde tu pantalla de inicio.',
      rotuloBoton: 'Instalar',
    };
  }
  return {
    mensaje: 'Instala Gnosis App y ábrela en su propia ventana, sin buscarla entre las pestañas.',
    rotuloBoton: 'Instalar',
  };
}
